import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { getActionByProjectType } from '../../actionMap';
import Layout from '../../components/Layout/Layout';
import { EProjectType } from '../../enum/project';
import './DesignCondition.scss';
import { ButtonPanel } from '../../components/ButtonPanel';
import { useProjectFormSetupModular as useProjectFormSetup } from '../../Dynamic/hooks/useProjectFormSetupModular';
import { logDebug, logError, logWarn, LogTag } from '../../utils/utils';
import { FormValuesProvider } from '../../contexts/FormValuesContext';
import { useAutoFetch } from '../../hooks/useAutoFetch';
import { usePdfExport } from '../../hooks/usePdfExport';
import { useCalculationOrchestrator } from '../../hooks/useCalculationOrchestrator';
import { useProjectData } from '../../hooks/useProjectData';
import { LoadingDisplay } from '../../components/LoadingDisplay';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { OperationAlerts } from '../../components/OperationAlerts';
import Map from '../../../components/Map/Map';
import { useAppDispatch } from '../../../reduxService/hooks';
import { updateProjectDetails } from '../../Dynamic/store/project/projectslice';
import { isMapImageGrey } from '../../utils/mapImage';

export const DesignCondition = () => {
    const location = useLocation();
    const dispatch = useAppDispatch();

    // Centralized project data access
    const { dynamicProjectData, projectType, resultsData, isFormSubmitting } = useProjectData();
    const [isApiSaving] = useState(false);

    // Track if map image has been captured in this session to avoid re-capturing
    const [mapImageCaptured, setMapImageCaptured] = useState(false);
    // Track if persisted mapImage is grey (failed prior capture); triggers recapture on next visit
    const [mapImageIsGrey, setMapImageIsGrey] = useState(false);
    // Live ref to current project guid for cross-render race protection (read by stale-capture guard
    // in handleMapImageUpdate). Updated synchronously each render so an in-flight capture from a
    // previously-loaded project cannot overwrite the now-current project's mapImage in Redux.
    const currentGuidRef = useRef<string | undefined>(undefined);
    currentGuidRef.current = dynamicProjectData?.guid;

    // Reset map-capture state when the loaded project guid changes (DesignCondition is reused
    // across `/designCondition/:id` and `/results/:id` route param changes — without this reset,
    // project A's mapImageCaptured/mapImageIsGrey would leak into project B's session).
    useEffect(() => {
        setMapImageCaptured(false);
        setMapImageIsGrey(false);
    }, [dynamicProjectData?.guid]);

    // Check if map image needs to be captured (missing from loaded project, or persisted one is grey)
    const needsMapCapture = useMemo(() => {
        if (!dynamicProjectData) return false;
        if (mapImageCaptured) return false;
        // Capture if mapImage is missing/empty OR if persisted one is grey (failed prior capture)
        return !dynamicProjectData.mapImage || mapImageIsGrey;
    }, [dynamicProjectData, mapImageCaptured, mapImageIsGrey]);

    // Get coordinates from project data
    const projectCoordinates = useMemo(() => {
        if (!dynamicProjectData?.projectInfo?.coordinates) {
            return { latitude: 44.5215, longitude: 11.2833 }; // Default coordinates
        }
        const coords = dynamicProjectData.projectInfo.coordinates;
        return {
            latitude: coords[0] || 44.5215,
            longitude: coords[1] || 11.2833,
        };
    }, [dynamicProjectData?.projectInfo?.coordinates]);

    // Callback to handle map image update
    const handleMapImageUpdate = useCallback(
        (mapImageUrl: string) => {
            if (!mapImageUrl || mapImageCaptured) return;
            // Stale-capture guard: a dom-to-image capture started for a previous project (before the user
            // navigated to /designCondition/<other>) can resolve after the current project is loaded.
            // The closure here was created when guid=capturedGuid; if Redux now points elsewhere,
            // dispatching would write the wrong project's map. Compare against the live ref.
            const capturedForGuid = dynamicProjectData?.guid;
            if (capturedForGuid !== currentGuidRef.current) return;
            // Dedupe: same string would still bump projectslice.lastModified and trigger downstream effects.
            // Also mark this visit's capture as consumed so the hidden Map can unmount even when retry produced
            // identical bytes — otherwise mapImageIsGrey would keep needsMapCapture=true for the rest of the visit.
            if (mapImageUrl === dynamicProjectData?.mapImage) {
                setMapImageCaptured(true);
                return;
            }

            logDebug('DesignCondition', 'Map image captured for loaded project', undefined, LogTag.PDF_IMAGES);
            dispatch(updateProjectDetails({ mapImage: mapImageUrl }));
            setMapImageCaptured(true);
        },
        [dispatch, mapImageCaptured, dynamicProjectData?.mapImage, dynamicProjectData?.guid]
    );

    // Classify persisted mapImage as grey/valid; if grey, re-arm needsMapCapture for one retry per visit.
    // Failure policy: treat decode errors as 'valid' (favor no-regression over self-healing) — a corrupted
    // dataURL that throws is rare, and defaulting to 'grey' could churn recapture forever if retries also fail.
    // The `guid` dep is required: if two projects share an identical mapImage string (e.g. both stored the
    // deterministic Leaflet grey placeholder), switching between them would skip reclassification without it.
    useEffect(() => {
        const img = dynamicProjectData?.mapImage;
        if (!img) {
            setMapImageIsGrey(false);
            return;
        }
        let cancelled = false;
        isMapImageGrey(img)
            .then(grey => {
                if (!cancelled) setMapImageIsGrey(grey);
            })
            .catch(err => {
                logWarn('DesignCondition', 'isMapImageGrey check failed; treating as valid (no recapture)', err, LogTag.PDF_IMAGES);
                if (!cancelled) setMapImageIsGrey(false);
            });
        return () => {
            cancelled = true;
        };
    }, [dynamicProjectData?.mapImage, dynamicProjectData?.guid]);

    // Auto-fetch hook for project data with retry logic
    const inResults = useMemo(() => location.pathname.split('/')[1] === 'results', [location.pathname]);
    const { autoFetchError, setAutoFetchError } = useAutoFetch(inResults, dynamicProjectData || null, resultsData);

    // PDF export hook for all PDF-related functionality - Only active when in results page
    const { isGeneratingPdfImages, pdfImageError, currentProjectImages, captureProjectImages, handleExportToPDF, clearPdfImageError } = usePdfExport(
        inResults ? dynamicProjectData || null : null, // Only pass data when in results
        projectType
    );

    // Ref for Formik instance - used by Execute to sync unsaved changes
    const formikRef = React.useRef<any>(null);

    // Calculation orchestrator hook for all calculation logic
    const { handleExecuteAction, handleSave } = useCalculationOrchestrator(dynamicProjectData || null, formikRef);

    // Other state and refs
    const divResults = React.useRef<HTMLDivElement>(null);
    const [, setShowGeneralResult] = React.useState(false);

    // Memoize currentProjectType calculation to avoid re-computation
    const currentProjectType = useMemo(() => projectType, [projectType]);

    // Memoize action components for the current project type
    const { inputForm: baseInputForm, graphic, results } = useMemo(() => getActionByProjectType(currentProjectType || EProjectType.container_yard), [currentProjectType]);

    // Clone inputForm with formikRef prop for Execute synchronization
    const inputForm = useMemo(() => React.cloneElement(baseInputForm as React.ReactElement, { formikRef }), [baseInputForm, formikRef]);

    const { loading: projectLoading, errorDialog: projectError } = useProjectFormSetup();

    React.useEffect(() => {
        setShowGeneralResult(!!resultsData);
    }, [resultsData]);

    // SessionStorage cleanup strategy - remove old entries on component mount with optimization
    useEffect(() => {
        const cleanupOldSessionStorageEntries = () => {
            try {
                const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
                const keysToCheck = Object.keys(sessionStorage).filter(key => key.includes('PdfImages_'));

                // Early exit if no keys to process
                if (keysToCheck.length === 0) {
                    logDebug('DesignCondition', 'No sessionStorage entries to cleanup', undefined, LogTag.PERFORMANCE);
                    return;
                }

                let cleanedCount = 0;
                const maxProcessBatch = 10; // Limit processing to prevent blocking

                keysToCheck.slice(0, maxProcessBatch).forEach(key => {
                    try {
                        const stored = sessionStorage.getItem(key);
                        if (stored) {
                            const parsed = JSON.parse(stored);
                            const age = Date.now() - (parsed.timestamp || 0);

                            if (age > MAX_AGE_MS) {
                                sessionStorage.removeItem(key);
                                cleanedCount++;
                                logDebug('DesignCondition', `Cleaned expired sessionStorage entry: ${key}`, undefined, LogTag.PERFORMANCE);
                            }
                        }
                    } catch (parseError) {
                        // Invalid JSON, remove it
                        sessionStorage.removeItem(key);
                        cleanedCount++;
                        logWarn('DesignCondition', `Removed corrupted sessionStorage entry: ${key}`, undefined, LogTag.PERFORMANCE);
                    }
                });

                if (cleanedCount > 0) {
                    logDebug('DesignCondition', `SessionStorage cleanup completed. Removed ${cleanedCount} entries.`, undefined, LogTag.PERFORMANCE);
                }
            } catch (error) {
                logError('DesignCondition', 'Error during sessionStorage cleanup:', error, LogTag.PERFORMANCE);
            }
        };

        // Debounce cleanup to prevent multiple rapid executions
        const cleanupTimer = setTimeout(cleanupOldSessionStorageEntries, 1000);

        return () => {
            if (cleanupTimer) {
                clearTimeout(cleanupTimer);
            }
        };
    }, []); // Run once on component mount

    // Cleanup sessionStorage flag when exiting DesignCondition page
    useEffect(() => {
        return () => {
            const projectGuid = dynamicProjectData?.guid;
            if (projectGuid) {
                try {
                    sessionStorage.removeItem(`geogridViewMode_${projectGuid}`);
                    logDebug('DesignCondition', `Cleaned up readonly mode flag for project ${projectGuid}`, undefined, LogTag.NAVIGATION);
                } catch (error) {
                    logWarn('DesignCondition', 'Failed to cleanup sessionStorage flag', error, LogTag.NAVIGATION);
                }
            }
        };
    }, []); // Cleanup only on unmount - guid is read inside cleanup function

    // Auto-fetch is now handled by the useAutoFetch hook

    if (projectLoading) {
        return <LoadingDisplay />;
    }

    if (projectError) {
        return <ErrorDisplay error={projectError} />;
    }

    return (
        <Layout>
            <FormValuesProvider>
                {/* Hidden Map component to capture map image for projects loaded without one */}
                {/* Uses opacity:0 instead of off-screen positioning to allow Leaflet tiles to load */}
                {needsMapCapture && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '418px',
                            height: '324px',
                            opacity: 0,
                            pointerEvents: 'none',
                            zIndex: -1,
                        }}>
                        <Map coordinates={projectCoordinates} onImageUpdate={handleMapImageUpdate} />
                    </div>
                )}
                <Grid container id="design-container">
                    <Grid className="side-bar" item xs={12} md={3} lg={3}>
                        {inputForm}
                    </Grid>
                    <Grid className="canvas-area" item xs={12} md={9} lg={9}>
                        <OperationAlerts
                            autoFetchError={autoFetchError}
                            pdfImageError={inResults ? pdfImageError : null} // Only show PDF errors in results page
                            onRetryAutoFetch={() => {
                                setAutoFetchError(null);
                                // Auto-fetch will retry automatically via the useAutoFetch hook
                            }}
                            onRetryPdfGeneration={
                                inResults
                                    ? () => {
                                          clearPdfImageError();
                                          captureProjectImages(currentProjectType);
                                      }
                                    : undefined
                            } // Only provide retry function in results page
                            isGeneratingPdf={inResults ? isGeneratingPdfImages : false} // Only show PDF loading in results page
                        />

                        {/* Calculate whether to show Export PDF button based on results validity */}
                        {(() => {
                            const shouldShowExportPdf = (() => {
                                // If not on results page, don't show PDF button
                                if (!inResults) return false;

                                // Container Yard validation is now handled by the usePdfExport hook
                                // For all project types, show if we're on results page
                                return true;
                            })();

                            return (
                                <ButtonPanel
                                    projectType={projectType}
                                    showSave={true}
                                    showExportPdf={shouldShowExportPdf}
                                    showGeoGrid={inResults}
                                    showExecute={!inResults}
                                    onSave={handleSave}
                                    onExecute={handleExecuteAction}
                                    saveDisabled={isFormSubmitting || isApiSaving}
                                    // Simplified: Only unified props needed
                                    useUnifiedProps={true}
                                    projectImages={currentProjectImages}
                                    isGeneratingPdfImages={isGeneratingPdfImages}
                                    pdfImageError={pdfImageError}
                                    onExportToPDF={handleExportToPDF}
                                />
                            );
                        })()}
                        <Grid item xs={12} ref={divResults}>
                            {inResults ? results : graphic}
                        </Grid>
                    </Grid>
                </Grid>
            </FormValuesProvider>
        </Layout>
    );
};
